/**
 * API service for Labor Standards
 */
import axios from "../utils/api-client";

const API_BASE_URL = `${process.env.REACT_APP_API_END_POINT}/LaborStandards`;

export interface OperationType {
	id: number;
	name: string;
	description?: string;
	defaultDurationMinutes?: number;
	sortOrder: number;
	isActive: boolean;
	createdDate: string;
	modifiedDate?: string;
}

export interface LaborStandardPhase {
	id: number;
	operationId: number;
	phaseId?: number;
	jobRole: string;
	/** FK to the company's Job — how a phase resolves to eligible workers. */
	jobRoleId?: number | null;
	/**
	 * FK to the company's configured work phase — the customer's own work-type
	 * code for this phase ("100 Installation", "Granulation"). Projected back as
	 * workPhaseCode / workPhaseName so a phase can be read in the customer's
	 * vocabulary as well as Wilya's job role.
	 */
	workPhaseId?: number | null;
	workPhaseCode?: string;
	workPhaseName?: string;
	workersRequired: number;
	sequence: number;
	startOffsetMinutes: number;
	durationMinutes: number;
	/**
	 * When true the phase's time is the EARLIEST it may start rather than the only
	 * time it can run: if nobody qualified is free, the allocator schedules it
	 * later instead of leaving it unstaffed. Meant for short sign-offs such as
	 * inspections; work that occupies the unit should stay fixed.
	 */
	isDeferrable?: boolean;
	/** Cap on how late a deferrable phase may start. Null = any time before the order ends. */
	maxDeferMinutes?: number | null;
	notes?: string;
	createdDate: string;
	modifiedDate?: string;
}

/**
 * An alternative work center an operation may run on ("compress on Fette
 * 3200i OR P3030"). `durationMinutes` overrides the operation's duration when
 * the step is planned on this option; null/undefined keeps the default.
 */
export interface LaborStandardWorkstationOption {
	id?: number;
	workstation: string;
	durationMinutes?: number | null;
	isDefault?: boolean;
	notes?: string;
}

export interface LaborStandardOperation {
	id: number;
	laborStandardId: number;
	/**
	 * The work center this step runs on. Required after the recipe-model
	 * migration; legacy standards may still have it unset, in which case the
	 * standard's header `workstation` applies to every operation.
	 */
	workstation?: string | null;
	/** Alternative work centers for this step, each with an optional duration override. */
	workstationOptions?: LaborStandardWorkstationOption[];
	// The customer-facing name of this operation ("Bed cap", "Ladder rack").
	// Server-side this falls back to the operation type's name when unset, so
	// it is safe to lead with. Prefer it over operationTypeName in the UI.
	name?: string;
	operationTypeId: number;
	operationType?: OperationType;
	// The operation's category — global master data shared across standards
	// ("Exterior Upfit", "Granulation & Blending"). Secondary context only.
	operationTypeName?: string;
	sequence: number;
	/**
	 * Operations sharing a parallel group run CONCURRENTLY — they start together
	 * and the group takes as long as its longest member. Null/undefined means the
	 * operation runs on its own, which is the sequential default.
	 */
	parallelGroup?: number | null;
	durationMinutes: number;
	notes?: string;
	phases?: LaborStandardPhase[];
	createdDate: string;
	modifiedDate?: string;
}

/**
 * An operation as SENT on create/update: the server assigns the identity and
 * timestamp columns, and recomputes durations, so the form must not be forced
 * to invent them.
 */
export type LaborStandardOperationInput = Omit<
	LaborStandardOperation,
	"id" | "laborStandardId" | "createdDate" | "modifiedDate" | "phases"
> & {
	phases?: Omit<LaborStandardPhase, "id" | "operationId" | "createdDate" | "modifiedDate">[];
};

/**
 * One labor standard per product (and recipe group). The work center lives on
 * each OPERATION; the header `workstation` is only the DEFAULT (the first
 * operation's) kept for display, and may be null.
 */
export interface LaborStandard {
	id: number;
	productId?: number;
	productName?: string;
	productItemNumber?: string;
	sku?: string;
	/** DEFAULT work center — the first operation's. Null for a standard with no operations. */
	workstation: string | null;
	/** e.g. SAP group number, "MAIN", "ALT"; null = the product's only recipe. */
	recipeGroup?: string | null;
	batchSize: number;
	/** Unit the batch size is expressed in ("KG", "EA", "L"). */
	batchUnit?: string | null;
	/** Every work center the standard has a step on (defaults and options). */
	workstations?: string[];
	totalDurationMinutes: number;
	description?: string;
	isActive: boolean;
	operations?: LaborStandardOperation[];
	createdDate: string;
	modifiedDate?: string;
}

export interface CreateWorkstationOptionDto {
	workstation: string;
	durationMinutes?: number | null;
	isDefault?: boolean;
	notes?: string;
}

export interface CreateLaborStandardDto {
	productId?: number;
	sku?: string;
	/**
	 * Optional header work center. The server copies it down to operations that
	 * lack one and sets it to the first operation's afterwards.
	 */
	workstation?: string | null;
	recipeGroup?: string | null;
	batchSize: number;
	batchUnit?: string | null;
	description?: string;
	operations?: CreateOperationDto[];
}

export interface CreateOperationDto {
	// Customer-facing operation name; when omitted the server falls back to the
	// operation type's name.
	name?: string;
	operationTypeId: number;
	sequence: number;
	/** Operations sharing this id run concurrently; null/omitted runs alone. */
	parallelGroup?: number | null;
	/** The work center this step runs on. Every operation must end with one. */
	workstation?: string | null;
	workstationOptions?: CreateWorkstationOptionDto[];
	durationMinutes: number;
	notes?: string;
	phases?: CreatePhaseDto[];
}

export interface CreatePhaseDto {
	jobRole: string;
	/** Short sign-offs may slide later rather than fail; see LaborStandardPhase. */
	isDeferrable?: boolean;
	maxDeferMinutes?: number | null;
	/** The company's work phase this labor phase belongs to, when known. */
	workPhaseId?: number | null;
	workersRequired: number;
	sequence: number;
	startOffsetMinutes: number;
	durationMinutes: number;
	notes?: string;
}

export interface UpdateLaborStandardDto {
	productId?: number;
	sku?: string;
	workstation?: string | null;
	recipeGroup?: string | null;
	batchSize?: number;
	batchUnit?: string | null;
	description?: string;
	isActive?: boolean;
	id?: number;
	operations?: CreateOperationDto[];
}

// ---------------------------------------------------------------------------
// Recipe parsing (POST /LaborStandards/parse-recipe)
//
// The server reads an .xlsx/.xls/.csv recipe export — an SAP PP-PI master
// recipe ("sap") or a long-format sheet with one row per phase ("long") — and
// returns the standards it would build, grouped by product, with each
// operation's raw and resolved work center. Nothing is saved by it.
// ---------------------------------------------------------------------------

export interface ParseRecipeOptions {
	/** Raw spreadsheet work-center text -> the company's work-center name. */
	workstationMap?: Record<string, string>;
	/** Work center -> the job role phases on it should resolve to. */
	workstationJobRoles?: Record<string, { id?: number; name?: string }>;
	/** The company's work-center names, so the parser can report unknown ones. */
	knownWorkstations?: string[];
	sheetName?: string;
}

export interface ParsedRecipePhase {
	jobRole?: string;
	jobRoleId?: number | null;
	/**
	 * The company's work phase this line matched, when the sheet carried a
	 * recognisable code. A MACHINE line arrives with workersRequired 0 and
	 * needsJobRole false: it occupies the unit but needs nobody.
	 */
	workPhaseCode?: string;
	workPhaseId?: number | null;
	/** Crew size as the sheet states it — may be 0 (machine) or fractional (0.5). */
	workersRequired: number;
	startOffsetMinutes: number;
	durationMinutes: number;
	laborHours?: number | null;
	machineHours?: number | null;
	/** True when the sheet carried no job role and the user must pick one. */
	needsJobRole: boolean;
}

export interface ParsedRecipeWorkstationOption {
	rawWorkstation: string;
	workstation?: string | null;
	durationMinutes?: number | null;
	fromRecipeGroup?: string | null;
}

export interface ParsedRecipeOperation {
	sequence: number;
	code?: string;
	name: string;
	rawWorkstation: string;
	/** Resolved company work center; null/empty when unknown. */
	workstation?: string | null;
	workstationOptions: ParsedRecipeWorkstationOption[];
	durationMinutes: number;
	operationTypeId?: number | null;
	phases: ParsedRecipePhase[];
}

export interface ParsedRecipeStandard {
	productKey: string;
	productName: string;
	productId?: number | null;
	recipeGroup?: string | null;
	batchSize: number;
	batchUnit?: string | null;
	description: string;
	/** Set when a standard for this product + recipe group already exists. */
	existingStandardId?: number | null;
	operations: ParsedRecipeOperation[];
}

export interface ParseRecipeResult {
	shape: "sap" | "long";
	sheetName: string;
	standards: ParsedRecipeStandard[];
	warnings: string[];
	unknownWorkstations: string[];
	unmatchedProducts: string[];
	rowsRead: number;
	zeroHourStepsSkipped: number;
}

// ---------------------------------------------------------------------------
// Job-order PDF parsing (POST /LaborStandards/parse-pdf)
//
// The server reads a customer job-order PDF and returns its structure: header
// facts, the operations it lists, and the labor "phases" under each operation.
// Phase codes here are DOCUMENT codes (100 Installation, 700 Service, 900
// Inspection) - NOT Wilya job roles. Codes the company has configured as work
// phases come back already matched, with the work phase's default job role as
// the suggestion; the rest the upload dialog still asks the user to map.
// ---------------------------------------------------------------------------

export interface JobOrderVehicle {
	vin?: string;
	unitNo?: string;
	year?: string;
	make?: string;
	model?: string;
}

export interface JobOrderPhase {
	/** Document phase code, e.g. "100", "700", "900". */
	phaseCode: string;
	description: string;
	hours: number;
	/**
	 * Set when phaseCode matched one of the company's configured work phases —
	 * that is what makes an import self-mapping. The suggested role is the
	 * matched work phase's default job role.
	 */
	matchedWorkPhaseId?: number | null;
	matchedWorkPhaseName?: string;
	suggestedJobRoleId?: number | null;
	suggestedJobRole?: string;
}

export interface JobOrderOperation {
	sequence: number;
	code?: string;
	name: string;
	totalHours: number;
	notes?: string;
	phases: JobOrderPhase[];
}

export interface JobOrderParseResult {
	detectedFormat: "holman-job-order" | "unknown";
	/** Job order number, e.g. "4082394". */
	sourceReference?: string;
	revision?: string;
	customer?: string;
	specCode?: string;
	description?: string;
	vehicle?: JobOrderVehicle;
	startDate?: string | null;
	/** The total the document itself declares, when it prints one. */
	declaredTotalHours?: number | null;
	/** The total the parser arrived at by summing the labor rows. */
	parsedTotalHours: number;
	/** True when parsedTotalHours agrees with declaredTotalHours. */
	totalsReconcile: boolean;
	matchedProductId?: number | null;
	matchedProductName?: string | null;
	operations: JobOrderOperation[];
	warnings: string[];
}

/**
 * Work orders that reference a labor standard, returned before an edit so the
 * user can see what a change would disturb. Mirrors
 * GET /api/LaborStandards/{id}/affected-mos.
 */
export interface AffectedMO {
	id: number;
	sku?: string;
	productName?: string;
	workstation: string;
	quantity: number;
	startTime: string;
	endTime?: string;
	assignmentCount: number;
	hasAssignments: boolean;
}

export interface AffectedMOsResponse {
	laborStandardId: number;
	totalAffectedMOs: number;
	mosWithAssignments: number;
	affectedMOs: AffectedMO[];
}

/** How long the roster reuses the standards it read. */
const STANDARDS_CACHE_MS = 5 * 60 * 1000;
let standardsCache: { at: number; pending: Promise<LaborStandard[]> } | null = null;

export const laborStandardsApi = {
	/**
	 * Parse a job-order PDF into an operation/phase structure for review.
	 * Nothing is persisted - the caller reviews, maps phases to job roles and
	 * then saves through create().
	 */
	parsePdf: async (file: File): Promise<JobOrderParseResult> => {
		const formData = new FormData();
		formData.append("file", file);
		const response = await axios.post<JobOrderParseResult>(
			`${API_BASE_URL}/parse-pdf`,
			formData,
			{
				headers: { "Content-Type": "multipart/form-data" },
				// Server-side PDF extraction on a long job order can be slow.
				timeout: 180000,
			}
		);
		return response.data;
	},

	/**
	 * Parse a recipe spreadsheet (.xlsx/.xls/.csv) into the standards it would
	 * create, for review. Nothing is persisted - the caller maps unknown work
	 * centers and job roles, then saves through create()/update().
	 */
	parseRecipe: async (file: File, options: ParseRecipeOptions = {}): Promise<ParseRecipeResult> => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("options", JSON.stringify(options));
		const response = await axios.post<ParseRecipeResult>(
			`${API_BASE_URL}/parse-recipe`,
			formData,
			{
				headers: { "Content-Type": "multipart/form-data" },
				timeout: 180000,
			}
		);
		return response.data;
	},

	/**
	 * Get all labor standards
	 */
	getAll: async (): Promise<LaborStandard[]> => {
		try {
			const response = await axios.get<LaborStandard[]>(API_BASE_URL);
			return response.data;
		} catch (error: any) {
			console.error("Failed to fetch labor standards:", error?.message);
			throw error;
		}
	},

	/**
	 * All labor standards, read at most once every few minutes: the roster asks on
	 * every plant and week it opens, and the list (hundreds of standards with their
	 * steps) rarely changes. Saving or deleting a standard here drops the copy.
	 */
	getAllCached: (): Promise<LaborStandard[]> => {
		if (!standardsCache || Date.now() - standardsCache.at > STANDARDS_CACHE_MS) {
			const pending = laborStandardsApi.getAll();
			standardsCache = { at: Date.now(), pending };
			pending.catch(() => {
				if (standardsCache?.pending === pending) standardsCache = null;
			});
		}
		return standardsCache.pending;
	},

	/** Forget the cached list (after a change made outside this module). */
	invalidateCache: (): void => {
		standardsCache = null;
	},

	/**
	 * Get a single labor standard by ID
	 */
	getById: async (id: number): Promise<LaborStandard> => {
		const response = await axios.get<LaborStandard>(`${API_BASE_URL}/${id}`);
		return response.data;
	},

	/**
	 * Get labor standards by product
	 */
	getByProduct: async (productId: number): Promise<LaborStandard[]> => {
		const response = await axios.get<LaborStandard[]>(`${API_BASE_URL}/product/${productId}`);
		return response.data;
	},

	/**
	 * Create a new labor standard
	 */
	create: async (standard: CreateLaborStandardDto): Promise<LaborStandard> => {
		standardsCache = null;
		const response = await axios.post<LaborStandard>(API_BASE_URL, standard);
		return response.data;
	},

	/**
	 * Update an existing labor standard
	 */
	update: async (id: number, standard: UpdateLaborStandardDto): Promise<LaborStandard> => {
		standardsCache = null;
		const response = await axios.put<LaborStandard>(`${API_BASE_URL}/${id}`, standard);
		return response.data;
	},

	/**
	 * Delete a labor standard
	 */
	/**
	 * Delete a labor standard. `permanent` maps to the API's ?permanent flag —
	 * without it the server soft-deletes, which silently contradicted callers
	 * that were already passing `true`.
	 */
	delete: async (id: number, permanent = false): Promise<void> => {
		standardsCache = null;
		await axios.delete(`${API_BASE_URL}/${id}`, { params: { permanent } });
	},

	/**
	 * Get all operation types
	 */
	getOperationTypes: async (): Promise<OperationType[]> => {
		try {
			const response = await axios.get<OperationType[]>(`${API_BASE_URL}/operation-types`);
			return response.data;
		} catch (error: any) {
			console.error("Failed to fetch operation types:", error?.message);
			throw error;
		}
	},

	/**
	 * Work orders that would be affected by editing this labor standard, with
	 * how many worker assignments each already has.
	 */
	getAffectedMOs: async (id: number): Promise<AffectedMOsResponse> => {
		try {
			const response = await axios.get<AffectedMOsResponse>(`${API_BASE_URL}/${id}/affected-mos`);
			return response.data;
		} catch (error: any) {
			console.error("Failed to fetch affected work orders:", error?.message);
			throw error;
		}
	},
};

export default laborStandardsApi;
